import { useState, useEffect, type ReactNode } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Login from "./Login";
import PartnerHub from "./App";
import AdminPanel from "./AdminPanel";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import { ContentRewardsDeckViewer, MirrormereDeckViewer } from "./pages/Decks";
import { supabase } from "./supabaseClient";
import { trackAdminAction } from "./hooks/useAdminTracker";
import { usePageTitle } from "./hooks/usePageTitle";

// Protected Route Component
const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const isAuthenticated = localStorage.getItem("app_authenticated") === "true";
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default function AuthWrapper() {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Update page title based on current route
  usePageTitle();

  const clearLocalStorage = () => {
    localStorage.removeItem("app_authenticated");
    localStorage.removeItem("app_email");
    localStorage.removeItem("app_role");
    localStorage.removeItem("app_user_id");
    localStorage.removeItem("app_permissions");
    
    // Clean legacy keys
    localStorage.removeItem("partner_authenticated");
    localStorage.removeItem("partner_email");
    localStorage.removeItem("partner_role");
    localStorage.removeItem("partner_id");
    localStorage.removeItem("user_permissions");
  };

  const refreshSession = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setLoading(false);
        return;
      }

      // Fetch Profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      // SECURITY CHECK: If no profile exists, this is likely a VDR visitor or unauthorized user.
      if (profileError || !profile) {
        console.warn("User has valid session but no profile. Denying access.");
        await supabase.auth.signOut();
        clearLocalStorage();
        navigate('/');
        setLoading(false);
        return;
      }

      // SECURITY CHECK: If user is suspended, deny access
      if (profile.is_suspended) {
        console.warn("User account is suspended. Denying access.");
        await supabase.auth.signOut();
        clearLocalStorage();
        localStorage.setItem("app_suspended", "true");
        localStorage.setItem("app_suspended_reason", profile.suspended_reason || "");
        navigate('/');
        setLoading(false);
        return;
      }

      // Fetch Page Permissions (Hierarchical)
      const { data: pagePerms } = await supabase
        .from('page_permissions')
        .select('page_key, category')
        .eq('user_id', session.user.id);

      // Fetch VDR Permissions
      const { data: vdrPerms } = await supabase
        .from('vdr_permissions')
        .select('room_id, access_level')
        .eq('user_id', session.user.id);

      // Fetch Domain Group Permissions
      const { data: dgPerms } = await supabase
        .from('domain_group_permissions')
        .select('group_id, access_level')
        .eq('user_id', session.user.id);

      const role = profile?.role || 'user';
      const isAdmin = role === 'super_admin';
      
      // Build permissions object
      const pages = isAdmin 
        ? ['admin.home', 'admin.users', 'admin.logs', 'fundraising.vdr', 'fundraising.domain_groups']
        : (pagePerms?.map(p => p.page_key) || []);
      
      const permissions = { 
        pages,
        vdr: vdrPerms || [],
        domainGroups: dgPerms || []
      };

      // Update Storage
      localStorage.setItem("app_authenticated", "true");
      localStorage.setItem("app_email", session.user.email || "");
      localStorage.setItem("app_role", role);
      localStorage.setItem("app_user_id", session.user.id);
      localStorage.setItem("app_permissions", JSON.stringify(permissions));

      // Redirect if on public pages
      if (location.pathname === '/' || location.pathname === '/login') {
         navigate('/admin');
      }
    } catch (e) {
      console.error("Session refresh failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial load
    refreshSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        refreshSession();
      } else if (event === 'SIGNED_OUT') {
        clearLocalStorage();
        navigate('/');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleLoginSuccess = () => {
    refreshSession();
  };

  const handleLogout = async () => {
    // Track logout before signing out (while we still have user info)
    await trackAdminAction('logout', {
      email: localStorage.getItem('app_email')
    });
    
    await supabase.auth.signOut();
    clearLocalStorage();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-neutral-600">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Login onLoginSuccess={handleLoginSuccess} />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Public Deck Routes - Password Protected */}
      <Route path="/decks/content-rewards" element={<ContentRewardsDeckViewer />} />
      <Route path="/decks/mirrormere" element={<MirrormereDeckViewer />} />
      
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute>
            <AdminPanel onLogout={handleLogout} />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/fundraising/*" 
        element={
          <ProtectedRoute>
            <AdminPanel onLogout={handleLogout} />
          </ProtectedRoute>
        } 
      />

      <Route
        path="/settings/*"
        element={
          <ProtectedRoute>
            <AdminPanel onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/holdings/*"
        element={
          <ProtectedRoute>
            <AdminPanel onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/partner/:partnerId/*" 
        element={
          <ProtectedRoute>
            <PartnerHub onLogout={handleLogout} />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}
